import { StorageKeys } from "@/common/constants/storageKeys";
import { openUserChat } from "@/common/helpers/teamsHelper";
import { HandIcon } from "@/common/icons";
import { Lockr } from "@/common/Lockr";
import { useMessage } from "@/common/messages";
import {
	useCallSessionStore,
	CallSessionState,
} from "@/common/stores/nonpersistingStores/callSession";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import {
	NavigationActions,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import { useQueuesStore, QueuesState } from "@/common/stores/nonpersistingStores/queues";
import { InteractionType, TransferChannelType, TransferType } from "@/common/types";
import { InteractionButton } from "@/components/atoms/interactionButton";
import { useAgentInteractionState } from "@/hooks/agent";
import {
	InteractionDto,
	InteractionStatus,
	MonitoringStartResponse,
	MonitorTypes,
	SuperviseCallDto,
} from "@/repository/models";
import { startTextMonitoring } from "@/repository/queryFns";
import { startMonitoring } from "@/repository/queryFns/supervisorInteractions";
import { tokens } from "@fluentui/react-components";
import {
	CommentMultiple24Regular,
	PeopleCommunity24Regular,
	PersonVoice24Regular,
} from "@fluentui/react-icons";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useTransferHook } from "../transfer";

export const useActiveInteractionsActions = () => {
	const { show: showMessage } = useMessage();
	const intl = useIntl();
	const currentUserSIP = Lockr.get(StorageKeys.SIP);

	const [monitoringStartInProgress, setMonitoringStartInProgress] =
		useState<boolean>(false);

	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const { agentHasCallInProgressOrTextInOffering } = useAgentInteractionState();

	const isSupervisorOnQueue = useCallback(
		(queueId: string) => {
			return queues.some((x) => x.id == queueId && x.isSupervisor);
		},
		[queues]
	);

	const [startWebchatTransfer] = useTransferHook(TransferChannelType.WebChat);
	const [startWhatsAppTransfer] = useTransferHook(TransferChannelType.WhatsApp);
	const [startEmailTransfer] = useTransferHook(TransferChannelType.Email);

	const setIsMobileDetailViewMode = useNavigationStore(
		(actions: NavigationActions) => actions.setIsMobileDetailViewMode
	);

	const startMonitoringAction = useCallback(
		(item: InteractionDto, type: MonitorTypes) => {
			const userId = Lockr.get(StorageKeys.UserId);

			if (item.type === InteractionType.Call) {
				startCallMonitoringAction(item, userId, type);
			}

			if (
				item.type === InteractionType.Email ||
				item.type === InteractionType.WhatsApp ||
				item.type === InteractionType.Webchat
			) {
				startTextMonitoringAction(item, userId);
			}
		},
		[]
	);

	const startCallMonitoringAction = useCallback(
		(item: InteractionDto, userId: string, type: MonitorTypes) => {
			const monitoringData = {
				scenarioId: item.id,
				supervisingAgentId: userId,
				monitoringType: type,
			} as SuperviseCallDto;
			setMonitoringStartInProgress(true);
			startMonitoring(monitoringData)
				.then((response: MonitoringStartResponse) => {					
					if (response !== MonitoringStartResponse.Success) {
						showMessage("allInteractions", {
							intent: "error",
							text: intl.formatMessage({
								id: `Transfer.ErrorMessage.MonitoringStartResponse.${
									MonitoringStartResponse[response]
								}`,
							}),
						});

						setMonitoringStartInProgress(false);
					} else {
						setIsMobileDetailViewMode(true);
					}
				})
				.catch(() => {
					setMonitoringStartInProgress(false);
				});
		},
		[]
	);

	const startTextMonitoringAction = useCallback(
		(item: InteractionDto, userId: string) => {
			setMonitoringStartInProgress(true);
			startTextMonitoring(item.id, userId)
				.then(() => {
					setIsMobileDetailViewMode(true);
				})
				.catch(() => {
					setMonitoringStartInProgress(false);
				});
		},
		[]
	);

	const shouldHideMonitoringInteractionButtonsByType = useCallback(
		(item: InteractionDto, userId: string) => {
			if (
				item.type !== InteractionType.Call &&
				item.type !== InteractionType.Email &&
				item.type !== InteractionType.WhatsApp &&
				item.type !== InteractionType.Webchat
			) {
				return true;
			}

			if (item.type === InteractionType.Call) {
				return callSession?.id === item.id;
			}

			if (
				item.type === InteractionType.Email ||
				item.type === InteractionType.WhatsApp ||
				item.type === InteractionType.Webchat
			) {
				return item?.agentId === userId;
			}

			return false;
		},
		[queues, callSession, monitoringStartInProgress, currentInteraction]
	);

	const shouldHideMonitoringButtons = useCallback(
		(item: InteractionDto) => {
			const warmTransferStatuses: InteractionStatus[] = [
				InteractionStatus.WarmTransferInitialized,
				InteractionStatus.WarmTransferRinging,
			];
			const monitoringDisabled = isMonitoringDisabled(item);
			const userId = Lockr.get(StorageKeys.UserId);
			const isCurrentItemMonitoredByUser =
				(callSession?.supervisorId === userId && callSession?.id === item.id) ||
				currentInteraction?.monitoringSupervisorId === userId ||
				item.monitoringSupervisorId === userId;

			if (
				item.monitoringType === MonitorTypes.BargeIn &&
				(item.type === InteractionType.Email ||
					item.type === InteractionType.WhatsApp ||
					item.type === InteractionType.Webchat)
			) {
				return true;
			}

			return (
				((agentHasCallInProgressOrTextInOffering ||
					item.monitoringSupervisorId === userId ||
					!isSupervisorOnQueue(item.queueId) ||
					item.isMonitoringHidden ||
					!item.isMonitoringEnabled ||
					shouldHideMonitoringInteractionButtonsByType(item, userId)) &&
					(!monitoringDisabled || isCurrentItemMonitoredByUser)) ||
				monitoringStartInProgress ||
				warmTransferStatuses.includes(item.status)
			);
		},
		[
			queues,
			callSession,
			monitoringStartInProgress,
			currentInteraction,
			agentHasCallInProgressOrTextInOffering,
		]
	);

	//CC4ALL-6542: button showing only for calls to be modified for all interactions in other story
	const shouldHideWhisperButton = useCallback(
		(item: InteractionDto) => {
			return (
				!isSupervisorOnQueue(item.queueId) ||
				item.type !== InteractionType.Call ||
				!item.agentUpn ||
				item.isUnify
			);
		},
		[queues]
	);

	const isMonitoringDisabled = useCallback(
		(item: InteractionDto) =>
			(item.isSupervised || item.isMonitoringStarted) ?? false,
		[]
	);

	const getTooltipMessage = useCallback(
		(item: InteractionDto, monitoringDisabled: boolean, type: MonitorTypes) =>
			monitoringDisabled
				? `${intl.formatMessage({
						id: "Interaction.Buttons.AlreadyMonitored",
					})} ${item.supervisorName ?? ""}`
				: intl.formatMessage({
						id: `Interaction.Buttons.${type === MonitorTypes.BargeIn ? "BargeIn" : "Listen"}`,
					}),
		[]
	);

	const getMonitoringTypeButton = useCallback(
		(item: InteractionDto) => {
			const monitoringDisabled = isMonitoringDisabled(item);

			if (shouldHideMonitoringButtons(item)) {
				return <></>;
			}

			return (
				monitoringDisabled && (
					<InteractionButton
						color="white"
						onClickCallBack={() => {}}
						icon={
							item.monitoringType === MonitorTypes.Silent ||
							item.type === InteractionType.Email ? (
								<PersonVoice24Regular
									primaryFill={tokens.colorNeutralForeground3}
								/>
							) : (
								<PeopleCommunity24Regular
									primaryFill={tokens.colorNeutralForeground3}
								/>
							)
						}
						disabled={true}
					/>
				)
			);
		},
		[agentHasCallInProgressOrTextInOffering, queues, callSession] //activeInteractions ca dependinta
	);

	const textVerificationByMonitoringType = (
		item: InteractionDto,
		type: MonitorTypes,
		input: boolean
	) => {
		return item.type === InteractionType.Email ||
			item.type === InteractionType.WhatsApp ||
			item.type === InteractionType.Webchat
			? type === MonitorTypes.Silent
			: input;
	};

	const getMonitoringActionButton = useCallback(
		(item: InteractionDto, type: MonitorTypes) => {
			const monitoringDisabled = isMonitoringDisabled(item);

			if (shouldHideMonitoringButtons(item)) {
				return <></>;
			}

			const showButton = monitoringDisabled
				? textVerificationByMonitoringType(
						item,
						type,
						item.monitoringType === type
					)
				: textVerificationByMonitoringType(item, type, true);

			return showButton ? (
				<InteractionButton
					tooltipMessage={getTooltipMessage(item, monitoringDisabled, type)}
					onClickCallBack={() => {
						startMonitoringAction(item, type);
					}}
					icon={
						type === MonitorTypes.Silent ? (
							<PersonVoice24Regular
								primaryFill={tokens.colorNeutralStrokeOnBrand2}
							/>
						) : (
							<PeopleCommunity24Regular
								primaryFill={tokens.colorNeutralStrokeOnBrand2}
							/>
						)
					}
					disabled={monitoringDisabled}
				/>
			) : (
				<></>
			);
		},
		[agentHasCallInProgressOrTextInOffering, queues, callSession] //activeInteractions ca dependinta
	);

	const getWhisperButton = useCallback(
		(item: InteractionDto) => {
			if (shouldHideWhisperButton(item)) {
				return <></>;
			}
			return (
				<InteractionButton
					tooltipMessage={intl.formatMessage({
						id: "Interaction.Buttons.Whisper",
					})}
					onClickCallBack={() => openUserChat(item.agentUpn ?? "")}
					icon={
						<CommentMultiple24Regular
							primaryFill={tokens.colorNeutralStrokeOnBrand2}
						/>
					}
					disabled={false}
				/>
			);
		},
		[agentHasCallInProgressOrTextInOffering, queues] //activeInteractions ca dependinta
	);

	const pickupConfigByType = {
		[InteractionType.WhatsApp]: { fn: startWhatsAppTransfer, type: TransferType.WhatsAppAgentTransfer },
		[InteractionType.Webchat]:  { fn: startWebchatTransfer,  type: TransferType.WebchatAgentTransfer  },
		[InteractionType.Email]:    { fn: startEmailTransfer,    type: TransferType.EmailAgentTransfer    },
	} as const;

	const getPickUpButton = useCallback(
		(interaction: InteractionDto) => {
			const userId = Lockr.get(StorageKeys.UserId);
			const isCurrentUserMonitoringThis =
				(callSession?.supervisorId === userId &&
					callSession?.id === interaction.id) ||
				interaction.monitoringSupervisorId === userId;

			if (isCurrentUserMonitoringThis) {
				return <></>;
			}

			const config = pickupConfigByType[interaction.type as keyof typeof pickupConfigByType];
			if (!config) {
				return <></>;
			}

			return (
				<InteractionButton
				tooltipMessage={intl.formatMessage({
					id: "Interaction.Buttons.PickUp",
				})}
				onClickCallBack={() => config.fn(config.type, currentUserSIP, interaction.id)}
				icon={
					<HandIcon />
				}
				disabled={false}
			/>
			);
		},
		[startWebchatTransfer, startWhatsAppTransfer, startEmailTransfer, currentUserSIP, intl, callSession]
	);
		
	return {
		isMonitoringDisabled,
		getWhisperButton,
		getMonitoringActionButton,
		getMonitoringTypeButton,
		setMonitoringStartInProgress,
		shouldHideMonitoringButtons,
		getPickUpButton,
	};
};
